import React, { useContext, useMemo, useState } from "react";
import { Link, useNavigate } from "react-router-dom";
import { CartContext } from "./CartContext";
import productData, {
  formatPrice,
  siteConfig,
} from "../Datas/productData";
import "../css/checkout.css";

const checkoutSteps = ["Contact", "Delivery", "Payment", "Review & pay"];

const initialForm = {
  email: "",
  marketing: false,
  country: "DE",
  firstName: "",
  lastName: "",
  company: "",
  address: "",
  apartment: "",
  postalCode: "",
  city: "",
  phone: "",
  paymentMethod: "card",
  cardNumber: "",
  expiry: "",
  securityCode: "",
  cardName: "",
  termsAccepted: false,
};

const getColorLabel = (color) => {
  if (!color) return "Default";
  return typeof color === "string" ? color : color.name || "Default";
};

const isValidExpiry = (value) => {
  const match = value.match(/^(0[1-9]|1[0-2])\/(\d{2})$/);
  if (!match) return false;
  const month = Number(match[1]);
  const year = 2000 + Number(match[2]);
  return new Date(year, month, 0, 23, 59, 59) >= new Date();
};

const CheckOut = () => {
  const navigate = useNavigate();
  const { cartItems, clearCart } = useContext(CartContext);
  const [currentStep, setCurrentStep] = useState(0);
  const [form, setForm] = useState(initialForm);
  const [errors, setErrors] = useState({});

  const lineItems = useMemo(
    () =>
      cartItems
        .map((item) => {
          const product = productData.find(({ id }) => id === item.id);
          return product ? { ...item, product } : null;
        })
        .filter(Boolean),
    [cartItems]
  );

  const subtotal = useMemo(
    () =>
      lineItems.reduce(
        (total, item) => total + item.product.price * item.quantity,
        0
      ),
    [lineItems]
  );
  const itemCount = lineItems.reduce(
    (total, item) => total + item.quantity,
    0
  );
  const deliveryCost =
    subtotal >= siteConfig.shipping.freeThreshold
      ? 0
      : siteConfig.shipping.standard;
  const total = subtotal + deliveryCost;

  const updateField = (event) => {
    const { checked, name, type, value } = event.target;
    setForm((current) => ({
      ...current,
      [name]: type === "checkbox" ? checked : value,
    }));
    setErrors((current) => ({ ...current, [name]: undefined }));
  };

  const updateCardNumber = (event) => {
    const digits = event.target.value.replace(/\D/g, "").slice(0, 19);
    const cardNumber = digits.replace(/(.{4})/g, "$1 ").trim();
    setForm((current) => ({ ...current, cardNumber }));
    setErrors((current) => ({ ...current, cardNumber: undefined }));
  };

  const updateExpiry = (event) => {
    const digits = event.target.value.replace(/\D/g, "").slice(0, 4);
    const expiry =
      digits.length > 2 ? `${digits.slice(0, 2)}/${digits.slice(2)}` : digits;
    setForm((current) => ({ ...current, expiry }));
    setErrors((current) => ({ ...current, expiry: undefined }));
  };

  const validateStep = (step) => {
    const nextErrors = {};

    if (step === 0) {
      if (!form.email.trim()) {
        nextErrors.email = "Enter your email address.";
      } else if (!/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(form.email)) {
        nextErrors.email = "Enter a valid email address.";
      }
    }

    if (step === 1) {
      if (!form.country) nextErrors.country = "Select a country.";
      if (!form.firstName.trim()) nextErrors.firstName = "Enter a first name.";
      if (!form.lastName.trim()) nextErrors.lastName = "Enter a last name.";
      if (!form.address.trim()) nextErrors.address = "Enter an address.";
      if (!form.postalCode.trim()) {
        nextErrors.postalCode = "Enter a postal code.";
      }
      if (!form.city.trim()) nextErrors.city = "Enter a city.";
    }

    if (step === 2 && form.paymentMethod === "card") {
      const cardDigits = form.cardNumber.replace(/\D/g, "");
      if (cardDigits.length < 13 || cardDigits.length > 19) {
        nextErrors.cardNumber = "Enter a valid card number.";
      }
      if (!isValidExpiry(form.expiry)) {
        nextErrors.expiry = "Enter a valid future expiry date.";
      }
      if (!/^\d{3,4}$/.test(form.securityCode)) {
        nextErrors.securityCode = "Enter a 3 or 4 digit security code.";
      }
      if (!form.cardName.trim()) {
        nextErrors.cardName = "Enter the name shown on the card.";
      }
    }

    if (step === 3 && !form.termsAccepted) {
      nextErrors.termsAccepted = "Accept the terms to place your order.";
    }

    return nextErrors;
  };

  const goToStep = (step) => {
    if (step <= currentStep) {
      setErrors({});
      setCurrentStep(step);
    }
  };

  const handleContinue = (event) => {
    event.preventDefault();
    const nextErrors = validateStep(currentStep);
    setErrors(nextErrors);
    if (Object.keys(nextErrors).length === 0) {
      setCurrentStep((step) => Math.min(step + 1, checkoutSteps.length - 1));
    }
  };

  const handlePlaceOrder = (event) => {
    event.preventDefault();
    const nextErrors = validateStep(3);
    setErrors(nextErrors);
    if (Object.keys(nextErrors).length > 0) return;

    const orderNumber = `${siteConfig.displayName.slice(0, 2)}-${Date.now()
      .toString()
      .slice(-8)}`;
    const paymentLabel =
      form.paymentMethod === "card"
        ? `Card ending ${form.cardNumber.replace(/\D/g, "").slice(-4)}`
        : form.paymentMethod === "klarna"
        ? "Klarna"
        : "PayPal";

    clearCart();
    navigate("/paysuccess", {
      replace: true,
      state: {
        email: form.email,
        itemCount,
        orderNumber,
        paymentLabel,
        total,
      },
    });
  };

  if (lineItems.length === 0) {
    return (
      <main className="checkout-page checkout-page--empty">
        <section className="checkout-empty" aria-labelledby="checkout-empty-title">
          <span>Checkout</span>
          <h1 id="checkout-empty-title">Your bag is empty</h1>
          <p>Add something to your bag before starting checkout.</p>
          <Link to="/shop">Return to shop</Link>
        </section>
      </main>
    );
  }

  return (
    <main className="checkout-page">
      <div className="checkout-page__header">
        <Link to="/cartpage">← Back to bag</Link>
        <p>Secure guest checkout</p>
      </div>

      <div className="checkout-page__layout">
        <div className="checkout-flow">
          <div className="checkout-title">
            <span>Checkout</span>
            <h1>Complete your order</h1>
          </div>

          <ol className="checkout-steps" aria-label="Checkout progress">
            {checkoutSteps.map((step, index) => (
              <li
                className={index <= currentStep ? "is-active" : ""}
                key={step}
              >
                <button
                  type="button"
                  disabled={index > currentStep}
                  onClick={() => goToStep(index)}
                  aria-current={index === currentStep ? "step" : undefined}
                >
                  <span>{index + 1}</span>
                  {step}
                </button>
              </li>
            ))}
          </ol>

          <form
            className="checkout-form"
            noValidate
            onSubmit={currentStep === 3 ? handlePlaceOrder : handleContinue}
          >
            {currentStep === 0 && (
              <section className="checkout-section" aria-labelledby="contact-title">
                <div className="checkout-section__heading">
                  <span>Step 1</span>
                  <h2 id="contact-title">Contact</h2>
                  <p>
                    Checkout as a guest. We will send your receipt and delivery
                    updates to this address.
                  </p>
                </div>

                <label className="checkout-field">
                  <span>Email address *</span>
                  <input
                    type="email"
                    name="email"
                    autoComplete="email"
                    value={form.email}
                    onChange={updateField}
                    aria-invalid={Boolean(errors.email)}
                    aria-describedby={errors.email ? "email-error" : undefined}
                  />
                  {errors.email && (
                    <small className="checkout-error" id="email-error">
                      {errors.email}
                    </small>
                  )}
                </label>

                <label className="checkout-checkbox">
                  <input
                    type="checkbox"
                    name="marketing"
                    checked={form.marketing}
                    onChange={updateField}
                  />
                  <span>Send me collection news and private offers.</span>
                </label>
              </section>
            )}

            {currentStep === 1 && (
              <section className="checkout-section" aria-labelledby="delivery-title">
                <div className="checkout-section__heading">
                  <span>Step 2</span>
                  <h2 id="delivery-title">Delivery</h2>
                  <p>Enter the address where you would like to receive your order.</p>
                </div>

                <label className="checkout-field">
                  <span>Country / region *</span>
                  <select
                    name="country"
                    value={form.country}
                    onChange={updateField}
                    aria-invalid={Boolean(errors.country)}
                  >
                    <option value="DE">Germany</option>
                    <option value="AT">Austria</option>
                    <option value="BE">Belgium</option>
                    <option value="FR">France</option>
                    <option value="IE">Ireland</option>
                    <option value="IT">Italy</option>
                    <option value="NL">Netherlands</option>
                    <option value="ES">Spain</option>
                  </select>
                  {errors.country && (
                    <small className="checkout-error">{errors.country}</small>
                  )}
                </label>

                <div className="checkout-field-row">
                  <label className="checkout-field">
                    <span>First name *</span>
                    <input
                      type="text"
                      name="firstName"
                      autoComplete="given-name"
                      value={form.firstName}
                      onChange={updateField}
                      aria-invalid={Boolean(errors.firstName)}
                    />
                    {errors.firstName && (
                      <small className="checkout-error">{errors.firstName}</small>
                    )}
                  </label>
                  <label className="checkout-field">
                    <span>Last name *</span>
                    <input
                      type="text"
                      name="lastName"
                      autoComplete="family-name"
                      value={form.lastName}
                      onChange={updateField}
                      aria-invalid={Boolean(errors.lastName)}
                    />
                    {errors.lastName && (
                      <small className="checkout-error">{errors.lastName}</small>
                    )}
                  </label>
                </div>

                <label className="checkout-field">
                  <span>Company (optional)</span>
                  <input
                    type="text"
                    name="company"
                    autoComplete="organization"
                    value={form.company}
                    onChange={updateField}
                  />
                </label>

                <label className="checkout-field">
                  <span>Address *</span>
                  <input
                    type="text"
                    name="address"
                    autoComplete="address-line1"
                    value={form.address}
                    onChange={updateField}
                    aria-invalid={Boolean(errors.address)}
                  />
                  {errors.address && (
                    <small className="checkout-error">{errors.address}</small>
                  )}
                </label>

                <label className="checkout-field">
                  <span>Apartment, suite, etc. (optional)</span>
                  <input
                    type="text"
                    name="apartment"
                    autoComplete="address-line2"
                    value={form.apartment}
                    onChange={updateField}
                  />
                </label>

                <div className="checkout-field-row checkout-field-row--city">
                  <label className="checkout-field">
                    <span>Postal code *</span>
                    <input
                      type="text"
                      name="postalCode"
                      autoComplete="postal-code"
                      value={form.postalCode}
                      onChange={updateField}
                      aria-invalid={Boolean(errors.postalCode)}
                    />
                    {errors.postalCode && (
                      <small className="checkout-error">{errors.postalCode}</small>
                    )}
                  </label>
                  <label className="checkout-field">
                    <span>City *</span>
                    <input
                      type="text"
                      name="city"
                      autoComplete="address-level2"
                      value={form.city}
                      onChange={updateField}
                      aria-invalid={Boolean(errors.city)}
                    />
                    {errors.city && (
                      <small className="checkout-error">{errors.city}</small>
                    )}
                  </label>
                </div>

                <label className="checkout-field">
                  <span>Phone (optional)</span>
                  <input
                    type="tel"
                    name="phone"
                    autoComplete="tel"
                    value={form.phone}
                    onChange={updateField}
                  />
                </label>

                <fieldset className="checkout-delivery-method">
                  <legend>Delivery method</legend>
                  <label>
                    <input type="radio" checked readOnly />
                    <span>
                      <strong>Standard delivery</strong>
                      <small>Estimated 3–5 working days</small>
                    </span>
                    <b>{deliveryCost === 0 ? "Free" : formatPrice(deliveryCost)}</b>
                  </label>
                </fieldset>
              </section>
            )}

            {currentStep === 2 && (
              <section className="checkout-section" aria-labelledby="payment-title">
                <div className="checkout-section__heading">
                  <span>Step 3</span>
                  <h2 id="payment-title">Payment</h2>
                  <p>
                    Choose a payment method. This storefront demonstration does
                    not process or store real payment details.
                  </p>
                </div>

                <fieldset className="checkout-payment-options">
                  <legend>Payment method</legend>
                  <label
                    className={
                      form.paymentMethod === "card" ? "is-selected" : ""
                    }
                  >
                    <input
                      type="radio"
                      name="paymentMethod"
                      value="card"
                      checked={form.paymentMethod === "card"}
                      onChange={updateField}
                    />
                    <span>Credit or debit card</span>
                    <span className="checkout-card-marks" aria-label="Visa Mastercard Amex">
                      <b>VISA</b><b>MC</b><b>AMEX</b>
                    </span>
                  </label>
                  <label
                    className={
                      form.paymentMethod === "klarna" ? "is-selected" : ""
                    }
                  >
                    <input
                      type="radio"
                      name="paymentMethod"
                      value="klarna"
                      checked={form.paymentMethod === "klarna"}
                      onChange={updateField}
                    />
                    <span>Klarna</span>
                    <small>Pay now or later</small>
                  </label>
                  <label
                    className={
                      form.paymentMethod === "paypal" ? "is-selected" : ""
                    }
                  >
                    <input
                      type="radio"
                      name="paymentMethod"
                      value="paypal"
                      checked={form.paymentMethod === "paypal"}
                      onChange={updateField}
                    />
                    <span>PayPal</span>
                    <small>Continue securely in the demo</small>
                  </label>
                </fieldset>

                {form.paymentMethod === "card" ? (
                  <div className="checkout-card-form">
                    <label className="checkout-field">
                      <span>Card number *</span>
                      <input
                        type="text"
                        name="cardNumber"
                        inputMode="numeric"
                        autoComplete="cc-number"
                        value={form.cardNumber}
                        onChange={updateCardNumber}
                        aria-invalid={Boolean(errors.cardNumber)}
                      />
                      {errors.cardNumber && (
                        <small className="checkout-error">{errors.cardNumber}</small>
                      )}
                    </label>
                    <div className="checkout-field-row">
                      <label className="checkout-field">
                        <span>Expiry (MM/YY) *</span>
                        <input
                          type="text"
                          name="expiry"
                          inputMode="numeric"
                          autoComplete="cc-exp"
                          value={form.expiry}
                          onChange={updateExpiry}
                          aria-invalid={Boolean(errors.expiry)}
                        />
                        {errors.expiry && (
                          <small className="checkout-error">{errors.expiry}</small>
                        )}
                      </label>
                      <label className="checkout-field">
                        <span>Security code *</span>
                        <input
                          type="text"
                          name="securityCode"
                          inputMode="numeric"
                          autoComplete="cc-csc"
                          maxLength="4"
                          value={form.securityCode}
                          onChange={updateField}
                          aria-invalid={Boolean(errors.securityCode)}
                        />
                        {errors.securityCode && (
                          <small className="checkout-error">
                            {errors.securityCode}
                          </small>
                        )}
                      </label>
                    </div>
                    <label className="checkout-field">
                      <span>Name on card *</span>
                      <input
                        type="text"
                        name="cardName"
                        autoComplete="cc-name"
                        value={form.cardName}
                        onChange={updateField}
                        aria-invalid={Boolean(errors.cardName)}
                      />
                      {errors.cardName && (
                        <small className="checkout-error">{errors.cardName}</small>
                      )}
                    </label>
                  </div>
                ) : (
                  <div className="checkout-provider-note">
                    <strong>
                      {form.paymentMethod === "klarna" ? "Klarna" : "PayPal"}
                    </strong>
                    <p>
                      In a live shop you would continue to the provider after
                      reviewing the order. No external request is made here.
                    </p>
                  </div>
                )}
              </section>
            )}

            {currentStep === 3 && (
              <section className="checkout-section" aria-labelledby="review-title">
                <div className="checkout-section__heading">
                  <span>Step 4</span>
                  <h2 id="review-title">Review & pay</h2>
                  <p>Check your details before placing the order.</p>
                </div>

                <div className="checkout-review">
                  <article>
                    <div>
                      <span>Contact</span>
                      <button type="button" onClick={() => goToStep(0)}>Edit</button>
                    </div>
                    <p>{form.email}</p>
                  </article>
                  <article>
                    <div>
                      <span>Delivery</span>
                      <button type="button" onClick={() => goToStep(1)}>Edit</button>
                    </div>
                    <p>
                      {form.firstName} {form.lastName}<br />
                      {form.address}
                      {form.apartment ? `, ${form.apartment}` : ""}<br />
                      {form.postalCode} {form.city}, {form.country}
                    </p>
                  </article>
                  <article>
                    <div>
                      <span>Payment</span>
                      <button type="button" onClick={() => goToStep(2)}>Edit</button>
                    </div>
                    <p>
                      {form.paymentMethod === "card"
                        ? `Card ending ${form.cardNumber.replace(/\D/g, "").slice(-4)}`
                        : form.paymentMethod === "klarna"
                        ? "Klarna"
                        : "PayPal"}
                    </p>
                  </article>
                </div>

                <label className="checkout-checkbox checkout-checkbox--terms">
                  <input
                    type="checkbox"
                    name="termsAccepted"
                    checked={form.termsAccepted}
                    onChange={updateField}
                    aria-invalid={Boolean(errors.termsAccepted)}
                  />
                  <span>
                    I agree to the <Link to="/terms">terms and conditions</Link>
                    {" "}and acknowledge the <Link to="/privacy">privacy policy</Link>.
                  </span>
                </label>
                {errors.termsAccepted && (
                  <small className="checkout-error checkout-error--terms">
                    {errors.termsAccepted}
                  </small>
                )}
              </section>
            )}

            <div className="checkout-actions">
              {currentStep > 0 && (
                <button
                  className="checkout-actions__back"
                  type="button"
                  onClick={() => {
                    setErrors({});
                    setCurrentStep((step) => step - 1);
                  }}
                >
                  Back
                </button>
              )}
              <button className="checkout-actions__primary" type="submit">
                {currentStep === 3
                  ? `Pay ${formatPrice(total)}`
                  : `Continue to ${checkoutSteps[currentStep + 1]}`}
              </button>
            </div>
          </form>
        </div>

        <aside className="checkout-summary" aria-labelledby="checkout-summary-title">
          <div className="checkout-summary__inner">
            <div className="checkout-summary__heading">
              <h2 id="checkout-summary-title">Your order</h2>
              <Link to="/cartpage">Edit bag</Link>
            </div>

            <div className="checkout-summary__items">
              {lineItems.map((item) => {
                const image = item.product.images?.[0] || item.product.image;
                const colorLabel = getColorLabel(item.color);
                return (
                  <article
                    className="checkout-summary-item"
                    key={`${item.id}-${item.size || "one-size"}-${colorLabel}`}
                  >
                    <div className="checkout-summary-item__image">
                      <img
                        src={image}
                        alt={item.product.imageAlt || item.product.name}
                      />
                      <span>{item.quantity}</span>
                    </div>
                    <div className="checkout-summary-item__details">
                      <h3>{item.product.name}</h3>
                      <p>{colorLabel} · {item.size || "One size"}</p>
                    </div>
                    <strong>
                      {formatPrice(item.product.price * item.quantity)}
                    </strong>
                  </article>
                );
              })}
            </div>

            <dl className="checkout-summary__totals">
              <div>
                <dt>Subtotal</dt>
                <dd>{formatPrice(subtotal)}</dd>
              </div>
              <div>
                <dt>Standard delivery</dt>
                <dd>{deliveryCost === 0 ? "Free" : formatPrice(deliveryCost)}</dd>
              </div>
              <div>
                <dt>Total</dt>
                <dd>{formatPrice(total)}</dd>
              </div>
            </dl>
            <p className="checkout-summary__note">VAT included</p>
          </div>
        </aside>
      </div>
    </main>
  );
};

export default CheckOut;
